import { AssignedConfigsDto, CompanyBasicInfoDto, PropertyGroupDetailDto, SenderIdentityDto } from '@inosio/payments-sdk';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ErrorType, RootState } from '../../../app/store';

export type PropertyGroupState = {
  propertyGroupDetails: PropertyGroupDetailDto; //specific property group in context for mostly non-pm type users
  isFetchingPropertyGroupDetails: boolean;
  isFetchingPropertyGroupDetailsSuccess: boolean;
  isFetchingPropertyGroupDetailsFailed: boolean;
  fetchPropertyGroupDetailsError: ErrorType;

  selectedPropertyGroupAssignedConfigs: AssignedConfigsDto; //granular property group configs for use mostly for PM type users

  allPropertyGroupsAssignedConfigs: AssignedConfigsDto[];
  shouldRefreshAllPropertyGroupConfigs: boolean; //*bit flag to indicate than configs have been updated and we need to fetch fresh copies.
  isFetchingAllPropertyGroupConfigs: boolean;
  isFetchingAllPropertyGroupConfigsSuccess: boolean;
  isFetchingAllPropertyGroupConfigsFailed: boolean;
  FetchAllPropertyGroupConfigsError: ErrorType;

  isUpdatingPropertyGroupConfigs: boolean;
  isUpdatingPropertyGroupConfigsSuccess: boolean;
  isUpdatingPropertyGroupConfigsFailed: boolean;
  UpdatingPropertyGroupConfigsError: ErrorType;
  isPropertyGroupAssignedConfigsRefreshRequired: boolean; //flag to help the UI know when to refresh the configs and when not to.

  propertyGroups: PropertyGroupDetailDto[]; //list of property groups a PM user has access to.
  isFetchingPropertyGroups: boolean;
  isFetchingPropertyGroupsSuccess: boolean;
  isFetchingPropertyGroupsFailed: boolean;

  companyInfo: CompanyBasicInfoDto; //had to add this for backwards compatibility
  isGettingCompany: boolean;
  isGettingCompanySuccess: boolean;
  isGettingCompanyFailed: boolean;
  companyError: ErrorType;

  //getting list of sender identities
  isFetchingSenderIdentities: boolean;
  isFetchingSenderIdentitiesFailed: boolean;
  isFetchingSenderIdentitiesSuccess: boolean;
  SenderIdentitiesResponseDto: any;
  SenderIdentities: SenderIdentityDto[];
  SenderIdentitiesFetchingError: ErrorType;

  //create new sender identity
  isCreatingSenderIdentity: boolean;
  isCreatingSenderIdentitySuccess: boolean;
  isCreatingSenderIdentityFailed: boolean;
  CreateSenderIdentityError: ErrorType;

  //delete sender identity
  isDeletingSenderIdentity: boolean;
  isDeletingSenderIdentitySuccess: boolean;
  isDeletingSenderIdentityFailed: boolean;
  DeleteSenderIdentityError: ErrorType;

  //update SenderIdentity
  isUpdatingSenderIdentity: boolean;
  isUpdatingSenderIdentitySuccess: boolean;
  isUpdatingSenderIdentityFailed: boolean;
  UpdateSenderIdentityError: ErrorType;
};

const getInitialState = () => {
  const intialState: PropertyGroupState = {
    propertyGroupDetails: {
      Description: '',
      Uuid: '',
      CompanyUuid: '',
      PropertyGroupType: '',
      Tag: '',
      CreatedDate: '',
    },
    isFetchingPropertyGroupDetails: false,
    isFetchingPropertyGroupDetailsSuccess: false,
    isFetchingPropertyGroupDetailsFailed: false,
    fetchPropertyGroupDetailsError: {
      message: '',
      statusCode: 0,
    },
    selectedPropertyGroupAssignedConfigs: {
      Uuid: '',
      Name: '',
    },
    allPropertyGroupsAssignedConfigs: [],
    shouldRefreshAllPropertyGroupConfigs: true,
    isFetchingAllPropertyGroupConfigs: false,
    isFetchingAllPropertyGroupConfigsSuccess: false,
    isFetchingAllPropertyGroupConfigsFailed: false,
    FetchAllPropertyGroupConfigsError: {
      message: '',
      statusCode: 0,
    },
    isUpdatingPropertyGroupConfigs: false,
    isUpdatingPropertyGroupConfigsSuccess: false,
    isUpdatingPropertyGroupConfigsFailed: false,
    UpdatingPropertyGroupConfigsError: {
      message: '',
      statusCode: 0,
    },
    isPropertyGroupAssignedConfigsRefreshRequired: false, //TODO do we still need?
    propertyGroups: [],
    isFetchingPropertyGroups: false,
    isFetchingPropertyGroupsSuccess: false,
    isFetchingPropertyGroupsFailed: false,
    companyInfo: {
      Uuid: '',
      DefaultPropertyGroupUuid: '',
    },
    isGettingCompany: false,
    isGettingCompanySuccess: false,
    isGettingCompanyFailed: false,
    companyError: {
      message: '',
      statusCode: 0,
    },

    isFetchingSenderIdentities: false,
    isFetchingSenderIdentitiesFailed: false,
    isFetchingSenderIdentitiesSuccess: false,
    SenderIdentitiesFetchingError: {} as ErrorType,
    SenderIdentitiesResponseDto: undefined,
    SenderIdentities: [],

    isCreatingSenderIdentity: false,
    isCreatingSenderIdentitySuccess: false,
    isCreatingSenderIdentityFailed: false,
    CreateSenderIdentityError: {} as ErrorType,

    isDeletingSenderIdentity: false,
    isDeletingSenderIdentitySuccess: false,
    isDeletingSenderIdentityFailed: false,
    DeleteSenderIdentityError: {} as ErrorType,

    isUpdatingSenderIdentity: false,
    isUpdatingSenderIdentitySuccess: false,
    isUpdatingSenderIdentityFailed: false,
    UpdateSenderIdentityError: {} as ErrorType,
  };

  return intialState;
};

const propertyGroupsSlice = createSlice({
  name: 'propertyGroups',
  initialState: getInitialState(),
  reducers: {
    getPropertyGroupDetails(state, action: { type: string; payload: { propertyGroupUuid: string; productTypeId?: number[] } }) {
      state.isFetchingPropertyGroupDetails = true;
      state.isFetchingPropertyGroupDetailsSuccess = false;
      state.isFetchingPropertyGroupDetailsFailed = false;
      state.propertyGroupDetails = {} as PropertyGroupDetailDto;
      state.fetchPropertyGroupDetailsError = {} as ErrorType;
    },
    setPropertyGroupDetails(state, action) {
      state.isFetchingPropertyGroupDetails = false;
      state.isFetchingPropertyGroupDetailsFailed = false;
      state.isFetchingPropertyGroupDetailsSuccess = true;
      state.propertyGroupDetails = action.payload;
    },
    setPropertyGroupDetailsError(state, action) {
      state.isFetchingPropertyGroupDetails = false;
      state.isFetchingPropertyGroupDetailsSuccess = false;
      state.isFetchingPropertyGroupDetailsFailed = true;
      state.fetchPropertyGroupDetailsError = action.payload;
    },
    getAllPropertyGroupsConfigs(state) {
      state.isFetchingAllPropertyGroupConfigs = true;
      state.isFetchingAllPropertyGroupConfigsSuccess = false;
      state.isFetchingAllPropertyGroupConfigsFailed = false;
      state.FetchAllPropertyGroupConfigsError = {} as ErrorType;
    },
    setAllPropertyGroupsConfigs(state, action) {
      state.isFetchingAllPropertyGroupConfigs = false;
      state.isFetchingAllPropertyGroupConfigsFailed = false;
      state.isFetchingAllPropertyGroupConfigsSuccess = true;
      state.shouldRefreshAllPropertyGroupConfigs = false;
      state.allPropertyGroupsAssignedConfigs = action.payload;

      //it is possible that this invocation happens during config updates. In that case, but sure to re-set the select prop group
      const existingSelectedPropGroupUuid = state.selectedPropertyGroupAssignedConfigs.Uuid;
      if (existingSelectedPropGroupUuid) {
        state.selectedPropertyGroupAssignedConfigs = state.allPropertyGroupsAssignedConfigs.find((apgac) => apgac.Uuid === existingSelectedPropGroupUuid) || ({} as AssignedConfigsDto);
      }
    },
    setAllPropertyGroupConfigsError(state, action) {
      state.isFetchingAllPropertyGroupConfigs = false;
      state.isFetchingAllPropertyGroupConfigsSuccess = false;
      state.isFetchingAllPropertyGroupConfigsFailed = true;
      state.FetchAllPropertyGroupConfigsError = action.payload;
    },
    selectSpecificPropertyGroupConfigs(
      state,
      action: {
        payload: { propertyGroupUuid: string };
        type: string;
      },
    ) {
      state.selectedPropertyGroupAssignedConfigs = state.allPropertyGroupsAssignedConfigs.find((apgac) => apgac.Uuid === action.payload.propertyGroupUuid) || ({} as AssignedConfigsDto);
    },
    getUpdatePropertyGroupConfigs(state, action) {
      state.isUpdatingPropertyGroupConfigs = true;
      state.isUpdatingPropertyGroupConfigsSuccess = false;
      state.isUpdatingPropertyGroupConfigsFailed = false;
      state.UpdatingPropertyGroupConfigsError = {} as ErrorType;

      //if it's qp or maintenance_emergency_criteria or application_questions, then set isPropertyGroupAssignedConfigsRefreshRequired = true. FYI, i'm not sure why we do this.
      if (action?.payload?.updatedPmConfig?.QuickPayTypes || action?.payload?.updatedPmConfig?.MaintenanceEmergencyCriteria || action?.payload?.updatedPmConfig?.ApplicationsQuestions)
        state.isPropertyGroupAssignedConfigsRefreshRequired = true;
    },
    setUpdatePropertyGroupConfigs(state, action) {
      state.isUpdatingPropertyGroupConfigs = false;
      state.isUpdatingPropertyGroupConfigsSuccess = true;
      state.isUpdatingPropertyGroupConfigsFailed = false;
      state.UpdatingPropertyGroupConfigsError = {} as ErrorType;
      state.shouldRefreshAllPropertyGroupConfigs = true;
      //TODO may need to add the action payload to something
    },
    setUpdatePropertyGroupConfigsError(state, action) {
      state.isUpdatingPropertyGroupConfigs = false;
      state.isUpdatingPropertyGroupConfigsSuccess = false;
      state.isUpdatingPropertyGroupConfigsFailed = true;
      state.UpdatingPropertyGroupConfigsError = action.payload;
    },
    clearPropertyGroupConfigSettingsFlags(state, action) {
      //TODO why is this needed?
      // state.isFetchingPropertyGroupConfigs = false;
      // state.isFetchingPropertyGroupConfigsSuccess = false;
      state.isUpdatingPropertyGroupConfigs = false;
      state.isUpdatingPropertyGroupConfigsSuccess = false;
      state.isUpdatingPropertyGroupConfigs = false;
      state.UpdatingPropertyGroupConfigsError = {} as ErrorType;
    },
    getPropertyGroups(state) {
      state.isFetchingPropertyGroups = true;
      state.isFetchingPropertyGroupsSuccess = false;
      state.isFetchingPropertyGroupsFailed = false;
    },
    setPropertyGroups(state, action) {
      state.isFetchingPropertyGroups = false;
      state.isFetchingPropertyGroupsSuccess = true;
      state.isFetchingPropertyGroupsFailed = false;
      state.propertyGroups = action.payload;
    },
    setPropertyGroupsError(state, action) {
      state.isFetchingPropertyGroups = false;
      state.isFetchingPropertyGroupsSuccess = false;
      state.isFetchingPropertyGroupsFailed = true;
    },
    getCompany(state, action) {
      state.isGettingCompany = true;
      state.isGettingCompanySuccess = false;
      state.isGettingCompanyFailed = false;
      state.companyError = {} as ErrorType;
    },
    handleGetCompanySuccess(state, action) {
      state.isGettingCompany = false;
      state.isGettingCompanySuccess = true;
      state.isGettingCompanyFailed = false;
      state.companyInfo = action.payload;
    },
    handleGetCompanyFailed(state, action) {
      state.isGettingCompany = false;
      state.isGettingCompanySuccess = false;
      state.isGettingCompanyFailed = true;
      state.companyError = action.payload;
    },
    getSenderIdentities(state, action) {
      state.isFetchingSenderIdentities = true;
      state.isFetchingSenderIdentitiesFailed = false;
      state.isFetchingSenderIdentitiesSuccess = false;
      state.SenderIdentitiesFetchingError = {} as ErrorType;
    },
    setSenderIdentities(state, action) {
      state.isFetchingSenderIdentities = false;
      state.isFetchingSenderIdentitiesSuccess = true;
      state.SenderIdentitiesFetchingError = {} as ErrorType;
      state.SenderIdentitiesResponseDto = action.payload;
      state.SenderIdentities = action.payload;
    },
    setSenderIdentitiesFetchError(state, action) {
      state.isFetchingSenderIdentities = false;
      state.isFetchingSenderIdentitiesFailed = true;
      state.isFetchingSenderIdentitiesSuccess = false;
      state.SenderIdentitiesFetchingError = action.payload;
    },
    createSenderIdentity(state, action) {
      state.isCreatingSenderIdentity = true;
      state.isCreatingSenderIdentityFailed = false;
      state.isCreatingSenderIdentitySuccess = false;
      state.CreateSenderIdentityError = {} as ErrorType;
    },
    setCreateSenderIdentity(state, action: PayloadAction<SenderIdentityDto>) {
      state.isCreatingSenderIdentity = false;
      state.isCreatingSenderIdentityFailed = false;
      state.isCreatingSenderIdentitySuccess = true;
      state.CreateSenderIdentityError = {} as ErrorType;
      state.SenderIdentities = [action.payload, ...state.SenderIdentities];
    },
    setCreateSenderIdentityFailed(state, action) {
      state.isCreatingSenderIdentity = false;
      state.isCreatingSenderIdentityFailed = true;
      state.isCreatingSenderIdentitySuccess = false;
      state.CreateSenderIdentityError = action.payload;
    },
    updateSenderIdentity(state, action) {
      state.isUpdatingSenderIdentity = true;
      state.isUpdatingSenderIdentityFailed = false;
      state.isUpdatingSenderIdentitySuccess = false;
      state.UpdateSenderIdentityError = {} as ErrorType;
    },
    setUpdateSenderIdentity(state, action: PayloadAction<SenderIdentityDto>) {
      state.isUpdatingSenderIdentity = false;
      state.isUpdatingSenderIdentityFailed = false;
      state.isUpdatingSenderIdentitySuccess = true;
      state.UpdateSenderIdentityError = {} as ErrorType;
      state.SenderIdentities = state.SenderIdentities.map((identity) => (identity.Id === action.payload.Id ? action.payload : identity));
    },
    setUpdateSenderIdentityFailed(state, action) {
      state.isUpdatingSenderIdentity = false;
      state.isUpdatingSenderIdentityFailed = true;
      state.isUpdatingSenderIdentitySuccess = false;
      state.UpdateSenderIdentityError = action.payload;
    },
    deleteSenderIdentity(state, action) {
      state.isDeletingSenderIdentity = true;
      state.isDeletingSenderIdentityFailed = false;
      state.isDeletingSenderIdentitySuccess = false;
      state.DeleteSenderIdentityError = {} as ErrorType;
    },
    setDeleteSenderIdentity(state, action) {
      state.isDeletingSenderIdentity = false;
      state.isDeletingSenderIdentityFailed = false;
      state.isDeletingSenderIdentitySuccess = true;
      state.DeleteSenderIdentityError = {} as ErrorType;
      state.SenderIdentities = state.SenderIdentities.filter((identity) => identity.Id !== action.payload);
    },
    setDeleteSenderIdentityFailed(state, action) {
      state.isDeletingSenderIdentity = false;
      state.isDeletingSenderIdentityFailed = true;
      state.isDeletingSenderIdentitySuccess = false;
      state.DeleteSenderIdentityError = action.payload;
    },
    resetCreateSenderIdentity(state) {
      state.isCreatingSenderIdentity = false;
      state.isCreatingSenderIdentityFailed = false;
      state.isCreatingSenderIdentitySuccess = false;
      state.CreateSenderIdentityError = {} as ErrorType;
    },
    resetDeleteSenderIdentity(state) {
      state.isDeletingSenderIdentity = false;
      state.isDeletingSenderIdentityFailed = false;
      state.isDeletingSenderIdentitySuccess = false;
      state.DeleteSenderIdentityError = {} as ErrorType;
    },
  },
});

export const {
  getPropertyGroupDetails,
  setPropertyGroupDetails,
  setPropertyGroupDetailsError,
  getAllPropertyGroupsConfigs,
  setAllPropertyGroupsConfigs,
  setAllPropertyGroupConfigsError,
  selectSpecificPropertyGroupConfigs,
  clearPropertyGroupConfigSettingsFlags,
  getPropertyGroups,
  setPropertyGroups,
  setPropertyGroupsError,
  getUpdatePropertyGroupConfigs,
  setUpdatePropertyGroupConfigs,
  setUpdatePropertyGroupConfigsError,
  getCompany,
  handleGetCompanySuccess,
  handleGetCompanyFailed,
  getSenderIdentities,
  setSenderIdentities,
  setSenderIdentitiesFetchError,
  createSenderIdentity,
  setCreateSenderIdentity,
  setCreateSenderIdentityFailed,
  updateSenderIdentity,
  setUpdateSenderIdentity,
  setUpdateSenderIdentityFailed,
  deleteSenderIdentity,
  setDeleteSenderIdentity,
  setDeleteSenderIdentityFailed,
  resetCreateSenderIdentity,
  resetDeleteSenderIdentity,
} = propertyGroupsSlice.actions;

export const selectPropertyGroupDetailsState = (state: RootState) => state.propertyGroupsState;

export default propertyGroupsSlice.reducer;
